import { getter } from '@/lib/utils/object';
import FormField from '@ingka/form-field';
import Switch from '@ingka/switch';
import React, { PropsWithChildren, ReactElement } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '../error-message';

export type ControlledSwitchProps<T extends FieldValues> = {
  className?: string;
  // Form
  name: FieldPath<T>;
  control: Control<T, any>;
  errors: FieldErrors<T>;
  rules?: UseControllerProps<T>['rules'];
  // Ingka form field props
  readOnly?: boolean;
  label: ReactElement | string;
  caption?: string;
  shouldValidate?: boolean;
  successMessage?: string;
  onChange?: (value: boolean) => void;
};

function ControlledSwitch<T extends FieldValues>(
  props: PropsWithChildren<ControlledSwitchProps<T>>
) {
  const { t } = useTranslation();
  const {
    name,
    control,
    errors,
    rules,
    shouldValidate = true,
    label,
    successMessage,
    readOnly,
    onChange,
  } = props;
  // Error handling
  const error = getter(errors, name);
  const errorMsgArgs = error && extractErrorMessage(error);
  const errorMsg = error && t(...errorMsgArgs);
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <FormField
          fieldHelper={{
            msg: shouldValidate ? successMessage : '',
            type: 'error',
          }}
          validation={{
            msg: <>{errorMsg}</>,
            type: 'error',
          }}
          valid={!error}
          shouldValidate={!!shouldValidate}
        >
          <Switch
            {...field}
            id={name}
            label={label}
            name={name}
            value={'true'}
            checked={field.value}
            disabled={readOnly}
            subtle
            onChange={(e) => {
              field.onChange(e.target.checked);
              if (onChange) onChange(e.target.checked);
            }}
          />
        </FormField>
      )}
    />
  );
}
export default ControlledSwitch;
